import axios from 'axios'
import {Map} from 'common'
import {getAuth, onAuthStateChanged, type User} from 'firebase/auth'
import type {FirebaseApp} from 'firebase/app'
import type {Auth} from 'firebase/auth'
import type {FirebaseStorage} from 'firebase/storage'
import {API_HOST_URL} from 'components/constants'
import {getAppCheckHeaders} from 'lib/api/appCheck'

type BrowserWithDadeAuth = Window & {
  __DADE_GET_ID_TOKEN__?: () => Promise<string | null>
}

export const ERRORS_CODE_MESSAGES_MAP: Map = {
  'auth/invalid-credential': 'Incorrect email or password.',
  'auth/invalid-login-credentials': 'Incorrect email or password.',
  'auth/wrong-password': 'Incorrect email or password.',
  'auth/user-not-found': "Email doesn't exist.",
  'auth/weak-password': 'Password should be at least 6 characters.',
  'auth/invalid-email': 'Invalid email.',
  'auth/too-many-requests':
    'You have sent too many requests. Please try again later.'
}

const getRegisteredIdToken = async () => {
  if (typeof window === 'undefined') {
    return null
  }

  const getIdToken = (window as BrowserWithDadeAuth).__DADE_GET_ID_TOKEN__
  if (typeof getIdToken !== 'function') {
    return null
  }

  try {
    return await getIdToken()
  } catch {
    return null
  }
}

const waitForBrowserUser = async () => {
  const auth = getAuth()

  if (auth.currentUser) {
    return auth.currentUser
  }

  return new Promise<User | null>((resolve) => {
    const timeoutId = window.setTimeout(() => {
      unsubscribe()
      resolve(auth.currentUser)
    }, 5000)

    const unsubscribe = onAuthStateChanged(
      auth,
      (user) => {
        window.clearTimeout(timeoutId)
        unsubscribe()
        resolve(user)
      },
      () => {
        window.clearTimeout(timeoutId)
        unsubscribe()
        resolve(auth.currentUser)
      }
    )
  })
}

export const getRequestHeaders = async () => {
  const token =
    (await getRegisteredIdToken()) ||
    (await waitForBrowserUser()
      .then((user) => user?.getIdToken())
      .catch(() => null))

  return {
    ...(await getAppCheckHeaders()),
    ...(token ? {Authorization: `Bearer ${token}`} : {})
  }
}

export type FirebaseInstances = {
  app: FirebaseApp
  auth: Auth
  firestore: unknown
  database: unknown
  storage: FirebaseStorage
}

export const handleAPIRequest = async (
  endpoint: string,
  method: string = 'GET',
  data: any = null
) => {
  try {
    const headers = await getRequestHeaders()
    const options = {
      method,
      url: `${API_HOST_URL}${endpoint}`,
      headers,
      ...(data && {data})
    }

    const rawResponse = await axios(options)
    const message = rawResponse.data.msg

    return {
      response: rawResponse,
      message
    }
  } catch (err: any) {
    const {response} = err
    const {data, status} = response

    return {
      error: data?.msg || data?.data || err.message,
      errorCode: status
    }
  }
}
